/* eslint-disable @typescript-eslint/no-explicit-any */

import { collectionAlias, CollectionName } from "@tc/search-document-builders"
import { defaultFieldRegistry } from "./fields/mappings"
import { transformValue } from "./lookupHelpers"
import {
  getValueByPathWithWildcards,
  pathContainsWildcard,
  setValueByPathWithWildcards,
} from "./utils/helper"
import type {
  DataConverter,
  FieldDefinition,
  IFieldRegistry,
  SearchFieldReresentation,
  ServiceConverterFactory,
  ServiceName,
  SubmissionLayout,
} from "./utils/types"
import { resolvePath } from "./utils/types"

const REGISTRATIONS_COLLECTION = collectionAlias(CollectionName.Registrations)
// createIdentityConverter remains the same
function createIdentityConverter(serviceName: ServiceName): DataConverter {
  console.warn(
    `FieldAnchor: Creating identity converter (deep clone) for service '${serviceName}'.`,
  )
  return (sourceData: Record<string, any>): Record<string, any> => {
    try {
      return structuredClone(sourceData)
    } catch (e) {
      console.error(
        `FieldAnchor: Failed structuredClone during identity conversion for '${serviceName}'. Falling back to shallow copy.`,
        e,
      )
      return { ...sourceData }
    }
  }
}
/**
 * Creates a DataConverter function that transforms data, including handling [*] wildcards for arrays.
 *
 * @param relevantDefinitions - An array of FieldDefinitions applicable to the specific from/to service pair.
 * @param fromService - The name of the source service.
 * @param targetService - The name of the target service.
 * @returns A DataConverter function for the specified transformation.
 */
const createEnhancedMappingConverter = (
  // Renamed
  allRelevantDefinitions: ReadonlyArray<FieldDefinition>,
  fromService: ServiceName,
  targetService: ServiceName,
  layout?: SubmissionLayout,
): DataConverter => {
  // Helper: get FieldDefinition by FUID
  const defByFuid = new Map(
    allRelevantDefinitions.map((def) => [def.fuid as string, def]),
  )

  // Main processor
  function processDataObject(
    sourceObject: any,
    targetObject: any,
    relevantFuids: string[],
  ) {
    const arrayPropsSet = new Set<string>()
    for (const fuid of relevantFuids) {
      const fieldDef = defByFuid.get(fuid)
      if (!fieldDef) continue
      const sourceRep = fieldDef.serviceRepresentations[fromService]
      const targetRep = fieldDef.serviceRepresentations[targetService]
      const sourcePath = resolvePath(sourceRep, layout)
      const targetPath = resolvePath(targetRep, layout)

      if (!sourcePath || !targetPath) continue

      // Use wildcard-aware utilities for all mappings
      const sourceValue = getValueByPathWithWildcards(sourceObject, sourcePath)

      // Count wildcards to determine if we need to flatten
      const sourceWildcardCount = (sourcePath.match(/\[\*\]/g) || []).length
      const targetWildcardCount = (targetPath.match(/\[\*\]/g) || []).length
      const needsFlatten = sourceWildcardCount > targetWildcardCount

      const isArrayTarget = pathContainsWildcard(targetPath)

      // Auto-flatten when source has more wildcards than target
      let valueToSet = sourceValue
      if (needsFlatten && Array.isArray(sourceValue)) {
        // For nested arrays like [[1], [2]], flatten each sub-array individually
        // to preserve the outer array structure for product-level mappings
        if (sourceValue.every((item) => Array.isArray(item))) {
          valueToSet = sourceValue.map((subArray) => {
            const filtered = subArray.filter(
              (v: any) => v !== null && v !== undefined,
            )
            return filtered.length > 0 ? filtered[0] : undefined
          })
        } else {
          const flattened = sourceValue
            .flat(Infinity)
            .filter((v) => v !== null && v !== undefined)
          valueToSet = flattened.length > 0 ? flattened[0] : undefined
        }
      }

      // Apply transformation for core lookup fields
      const isToGraphQL = fromService === "DFE"
      const isDfeToCore =
        isToGraphQL &&
        (targetService === "CORE_PHYTO_INPUT" ||
          targetService === "CORE_EXPORT_INPUT" ||
          targetService === "CORE_TRANSFER_INPUT")
      const isCoreToDfe =
        !isToGraphQL &&
        (fromService === "CORE_PHYTO_RESPONSE" ||
          fromService === "CORE_EXPORT_RESPONSE" ||
          fromService === "CORE_TRANSFER_RESPONSE") &&
        targetService === "DFE"
      if (isDfeToCore || isCoreToDfe) {
        valueToSet = transformValue(
          valueToSet, // The value to transform
          fieldDef, // Field definition containing transformation rules
          isToGraphQL, // Direction: true = to GraphQL format, false = from GraphQL
          fromService,
          targetService,
        )
      }

      const shouldSet =
        valueToSet !== undefined &&
        (needsFlatten ? valueToSet !== null && valueToSet !== "" : true)
      if (shouldSet) {
        try {
          if (isArrayTarget) {
            arrayPropsSet.add(targetPath.split("[")[0])
          }
          if (isArrayTarget && !Array.isArray(valueToSet)) {
            setValueByPathWithWildcards(targetObject, targetPath, [valueToSet])
          } else {
            setValueByPathWithWildcards(targetObject, targetPath, valueToSet)
          }
        } catch (err) {
          console.error(
            `FieldAnchor Mapping Error: FUID=${fuid}, sourcePath='${sourcePath}', targetPath='${targetPath}'`,
            err,
          )
        }
      }
    }
    // Remove empty arrays from target
    for (const prop of arrayPropsSet) {
      if (
        Array.isArray(targetObject[prop]) &&
        targetObject[prop].length === 0
      ) {
        delete targetObject[prop]
      }
    }
  }

  // The converter function
  return (sourceData: Record<string, any>): Record<string, any> => {
    const targetData: Record<string, any> = {}
    const allFuids = allRelevantDefinitions.map((def) => def.fuid as string)
    processDataObject(sourceData, targetData, allFuids)
    return targetData
  }
}

/**
 * Creates a ServiceConverterFactory that produces DataConverter functions for transforming data between services.
 * Handles wildcard array mappings and filters definitions relevant to specific service pairs.
 *
 * @param registry - Field registry containing field definitions (defaults to defaultFieldRegistry)
 * @returns ServiceConverterFactory function that creates converters for service pairs
 */
export const makeConverterFactory = (
  registry: IFieldRegistry = defaultFieldRegistry,
): ServiceConverterFactory => {
  const allDefinitions = Array.from(registry.values())

  return (
    fromService: ServiceName,
    targetService: ServiceName,
    layout?: SubmissionLayout,
  ): DataConverter => {
    if (fromService === targetService) {
      return createIdentityConverter(fromService)
    } else {
      // Filter definitions relevant for THIS specific service pair *once*
      // Need to check if representations exist for *both* services
      const relevantDefinitions = allDefinitions.filter(
        (def) =>
          def.serviceRepresentations[fromService] &&
          def.serviceRepresentations[targetService],
      )

      if (relevantDefinitions.length === 0) {
        console.warn(
          `FieldAnchor (${fromService}->${targetService}): No relevant field definitions found for this service pair. Converter will produce empty objects.`,
        )
        // Return a converter that does nothing but return empty
        return (_sourceData: Record<string, any>): Record<string, any> => ({})
      }

      // Create the converter using the relevant definitions
      // ** Call the new enhanced function **
      return createEnhancedMappingConverter(
        relevantDefinitions,
        fromService,
        targetService,
        layout,
      )
    }
  }
}

/**
 * Creates a curried function for getting search representations by submission layout and FUID.
 *
 * @param registry - Field registry to use (defaults to defaultFieldRegistry)
 * @returns A curried function that takes submissionLayout, then fuid, returning search representation or null
 *
 * @example
 * ```typescript
 * // Create custom getter with specific registry
 * const customGetter = createSearchRepresentationGetter(myRegistry)
 * const apExportGetter = customGetter("AP_EXPORT")
 * const searchValue = apExportGetter("some-field-id")
 *
 * // Or chain directly
 * const result = createSearchRepresentationGetter()("AP_EXPORT")("field-id")
 * ```
 */
const createSearchRepresentationGetter =
  (registry: IFieldRegistry = defaultFieldRegistry) =>
  (submissionLayout: SubmissionLayout) =>
  (fuid: string): SearchFieldReresentation | null => {
    const fieldDef = registry.get(fuid as any)
    return fieldDef?.searchRepresentation?.[submissionLayout] ?? null
  }

/**
 * Pre-configured search representation getter using the default field registry.
 *
 * @example
 * ```typescript
 * // Direct usage
 * const searchValue = getDefaultSearchRepresentation("AP_EXPORT")("some-field-id")
 *
 * // Partial application for reuse
 * const apExportSearch = getDefaultSearchRepresentation("AP_EXPORT")
 * const searchValue1 = apExportSearch("field-1")
 * const searchValue2 = apExportSearch("field-2")
 * ```
 */
export const getDefaultSearchRepresentation = createSearchRepresentationGetter()

/**
 * Gets the appropriate API key type based on usePermissions flag
 * @param usePermissions - Whether to use permission-restricted API key
 * @param collectionName - Collection name for specific key selection
 * @returns API key type identifier:
 *   - For 'private:registrations': 'registration' (restricted) or 'registrationAll' (unrestricted)
 *   - For other collections: 'default'
 */
export const getApiKeyType = (
  usePermissions?: boolean,
  collectionName?: string,
): string => {
  if (collectionName === REGISTRATIONS_COLLECTION) {
    return usePermissions ? "registration" : "registrationAll"
  }
  return "default"
}

/**
 * Enhanced search representation getter that includes API key selection
 * @param registry - Field registry to use
 * @returns Function that takes submission layout and returns enhanced search representation with apiKeyType field
 *
 * The apiKeyType field indicates which API key to use:
 * - 'registration': User-restricted registration search (usePermissions=true)
 * - 'registrationAll': Unrestricted registration search (usePermissions=false)
 * - 'default': Standard key handling for other collections
 */
const createEnhancedSearchRepresentationGetter =
  (registry: IFieldRegistry = defaultFieldRegistry) =>
  (submissionLayout: SubmissionLayout) =>
  (
    fuid: string,
  ): (SearchFieldReresentation & { apiKeyType: string }) | null => {
    const fieldDef = registry.get(fuid as any)
    const searchRep = fieldDef?.searchRepresentation?.[submissionLayout]

    if (!searchRep) return null

    return {
      ...searchRep,
      apiKeyType: getApiKeyType(
        searchRep.usePermissions,
        searchRep.collectionName,
      ),
    }
  }

/**
 * Enhanced search representation getter with API key selection
 *
 * @example
 * ```typescript
 * const searchRep = getEnhancedSearchRepresentation("AP_EXPORT")("PARTY_CONSIGNOR_ID")
 * if (searchRep) {
 *   console.log(searchRep.collectionName) // "private:registrations"
 *   console.log(searchRep.usePermissions) // true
 *   console.log(searchRep.apiKeyType)     // "registration" or "registrationAll"
 * }
 * ```
 */
export const getEnhancedSearchRepresentation =
  createEnhancedSearchRepresentationGetter()

/**
 * Finds a field definition by service path from any service representation.
 *
 * @param servicePath - The path to search for in service representations
 * @param registry - Field registry to search in (defaults to defaultFieldRegistry)
 * @returns Object containing fuid, serviceRepresentations, and searchRepresentation, or null if not found
 */
export const findFieldByServicePath = (
  servicePath: string,
  registry: IFieldRegistry = defaultFieldRegistry,
): {
  fuid: string
  serviceRepresentations: FieldDefinition["serviceRepresentations"]
  searchRepresentation?: FieldDefinition["searchRepresentation"]
} | null => {
  // Normalize the input path to handle both formats
  const normalizedInput = servicePath.replaceAll("[*]", "")

  const matchesPath = (path: string) => {
    const normalizedPath = path.replaceAll("[*]", "")
    return normalizedPath === normalizedInput || path === servicePath
  }

  for (const fieldDef of registry.values()) {
    for (const [, representation] of Object.entries(
      fieldDef.serviceRepresentations,
    )) {
      // Simple path
      if ("path" in representation) {
        if (matchesPath(representation.path)) {
          return {
            fuid: fieldDef.fuid as string,
            serviceRepresentations: fieldDef.serviceRepresentations,
            searchRepresentation: fieldDef.searchRepresentation,
          }
        }
      } else {
        // Layout-keyed: check each layout path
        for (const layoutRep of Object.values(representation)) {
          if (layoutRep.path && matchesPath(layoutRep.path)) {
            return {
              fuid: fieldDef.fuid as string,
              serviceRepresentations: fieldDef.serviceRepresentations,
              searchRepresentation: fieldDef.searchRepresentation,
            }
          }
        }
      }
    }
  }
  return null
}

/**
 * Find a field by service path that has a searchRepresentation for the given context.
 * Used when multiple fields share the same service path but only one is relevant for the current context.
 */
export const findFieldByServicePathForContext = (
  servicePath: string,
  contextKey: SubmissionLayout,
  registry: IFieldRegistry = defaultFieldRegistry,
): {
  fuid: string
  serviceRepresentations: FieldDefinition["serviceRepresentations"]
  searchRepresentation?: FieldDefinition["searchRepresentation"]
} | null => {
  const normalizedInput = servicePath.replaceAll("[*]", "")

  const matchesPath = (path: string) => {
    const normalizedPath = path.replaceAll("[*]", "")
    return normalizedPath === normalizedInput || path === servicePath
  }

  for (const fieldDef of registry.values()) {
    // Skip fields that don't have a searchRepresentation for this context
    if (!fieldDef.searchRepresentation?.[contextKey]) continue

    for (const [, representation] of Object.entries(
      fieldDef.serviceRepresentations,
    )) {
      if ("path" in representation) {
        if (matchesPath(representation.path)) {
          return {
            fuid: fieldDef.fuid as string,
            serviceRepresentations: fieldDef.serviceRepresentations,
            searchRepresentation: fieldDef.searchRepresentation,
          }
        }
      } else {
        for (const layoutRep of Object.values(representation)) {
          if (layoutRep.path && matchesPath(layoutRep.path)) {
            return {
              fuid: fieldDef.fuid as string,
              serviceRepresentations: fieldDef.serviceRepresentations,
              searchRepresentation: fieldDef.searchRepresentation,
            }
          }
        }
      }
    }
  }
  return null
}

// --- Default Instance and Exports ---
const createConvertor: ServiceConverterFactory = makeConverterFactory()
export default createConvertor
// makeConverterFactory is already exported by name
